import {
  IconMoodBoy,
  IconTrash,
  IconUser,
  IconUserHeart,
  IconUsers,
} from '@tabler/icons-react';
import { InsuredSubtype } from '@zorro/clients';
import {
  Accordion,
  type AccordionStyleVariant,
} from '@zorro/design-system/v2/Accordion';
import { Badge } from '@zorro/design-system/v2/Badge';
import { Button } from '@zorro/design-system/v2/Button';
import { ThemeIcon } from '@zorro/design-system/v2/ThemeIcon';
import { Grid, Group, Icon, Text, brand } from '@zorro/web/design-system';
import { useMobileDetector } from '@zorro/web/ui-utils';
import { ReactNode, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useFieldArray } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import {
  DependentForm,
  InsuredDependentFormFields,
} from './DependentsFormInputs';
import { EmployeeFormInputs } from './EmployeeFormInputs';
import {
  UseInsuredFormReturnType,
  getDefaultInsuredFormFields,
  getIsDependentValid,
} from './InsuredFormUtils';
import { SpouseFormInputs } from './SpouseFormInputs';

// `null` rather than `undefined` keeps the date input controlled, so it renders empty
type NewDependentFormFields = Omit<
  InsuredDependentFormFields,
  'dateOfBirth'
> & {
  dateOfBirth: Date | null;
};

const ADDED_SECTION_STYLE = {
  scrollMarginTop:
    'calc(var(--app-layout-header-height, 0px) + var(--app-layout-subheader-height, 0px))',
};

type Props = {
  insuredForm: UseInsuredFormReturnType;
  employerId: string;
  onboardingPeriodId?: string;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  isGlobalMode?: boolean;
  withAnticipatedCareLevel?: boolean;
  accordionStyleVariant?: AccordionStyleVariant;
};

export const InsuredFormInputs = ({
  insuredForm,
  employerId,
  onboardingPeriodId,
  isFinalizationMode,
  isEmployeeMode,
  isGlobalMode = false,
  withAnticipatedCareLevel = false,
  accordionStyleVariant = 'border',
}: Props) => {
  const isUnderlined = accordionStyleVariant === 'underlined';
  const controlPx = isUnderlined ? undefined : 'xl';
  const panelPx = isUnderlined ? undefined : '0.25rem';
  const panelStyles = isUnderlined
    ? { content: { paddingBottom: 'var(--space-24)' } }
    : undefined;
  const sectionMt = isUnderlined ? 'var(--space-20)' : 'var(--space-8)';
  const {
    employeeForm,
    spouseForm,
    dependentsForm,
    formValidity,
    hasAttemptedSubmit,
    dependentSchema,
    hasAnyDependents,
    targetEnrollmentDate: effectiveFrom,
    spouseStatus,
    setSpouseStatus,
    openedTabs,
    setOpenedTabs,
    isDomesticPartnerAllowed,
    withResidentialAddress,
  } = insuredForm;
  const { sendEvent } = useAnalytics();
  const { isMobile } = useMobileDetector();
  const spouseSectionRef = useRef<HTMLDivElement>(null);
  const lastDependentSectionRef = useRef<HTMLDivElement>(null);

  const fullNameEmployee = `${employeeForm.watch('firstName') || ''} ${
    employeeForm.watch('lastName') || ''
  }`.trim();
  const fullNameSpouse = `${spouseForm.watch('firstName') || ''} ${
    spouseForm.watch('lastName') || ''
  }`.trim();
  const employeeLabel = isEmployeeMode ? 'About you' : 'Employee info';
  const spouseSubtypeLabel =
    spouseForm.watch('subtype') === InsuredSubtype.DOMESTIC_PARTNER
      ? 'Domestic partner'
      : 'Spouse';

  const handleSendEvent = async (eventName: string) => {
    await sendEvent(
      eventName,
      onboardingPeriodId
        ? {
            onboarding_period_id: onboardingPeriodId,
          }
        : undefined
    );
  };

  const handleDeleteSpouse = async () => {
    spouseForm.reset(getDefaultInsuredFormFields());

    setSpouseStatus({
      spouseWasRemoved: true,
      isSpouseActive: false,
    });

    await handleSendEvent('remove_spouse');
  };

  const employeeAddress = employeeForm.watch('residentialAddress');
  const dependentValues = dependentsForm.watch('dependents');
  const {
    append: appendDependent,
    remove,
    fields: dependents,
  } = useFieldArray({
    control: dependentsForm.control,
    name: 'dependents',
  });

  const handleAppendDependent = async () => {
    await employeeForm.trigger();
    if (hasAnyDependents) {
      await dependentsForm.trigger();
    }
    const newDependent: NewDependentFormFields = {
      ...getDefaultInsuredFormFields(employeeAddress),
      dateOfBirth: null,
      isDisabled: false,
    };

    const newDependentIndex = dependentsForm.getValues().dependents.length;

    // Committing the section already expanded skips the collapse animation, which
    // would otherwise clamp the scroll to the not-yet-grown page height.
    flushSync(() => {
      appendDependent(newDependent as InsuredDependentFormFields);
      setOpenedTabs((prevTab) => [
        ...prevTab,
        `DEPENDENT-${newDependentIndex}`,
      ]);
    });
    lastDependentSectionRef.current?.scrollIntoView({ block: 'start' });
    await handleSendEvent('add_dependent');
  };

  const handleAddSpouse = async () => {
    if (!spouseStatus.isSpouseActive) {
      flushSync(() => {
        setSpouseStatus((prev) => ({
          ...prev,
          isSpouseActive: true,
        }));
        setOpenedTabs((prevTab) => [...prevTab, 'SPOUSE']);
      });
      spouseForm.setFocus('firstName');
      spouseSectionRef.current?.scrollIntoView({ block: 'start' });

      await handleSendEvent('add_spouse');
    }
  };

  const handleChange = async (value: string[]) => {
    await insuredForm.employeeForm.trigger();

    if (spouseStatus.isSpouseActive && openedTabs.includes('SPOUSE')) {
      await insuredForm.spouseForm.trigger();
    }
    if (hasAnyDependents) {
      await insuredForm.dependentsForm.trigger();
    }
    setOpenedTabs(value);
  };

  const getDependentTitle = (index: number, isMobile: boolean) => {
    const dependent = dependentsForm.getValues().dependents[index];
    const fullName = `${dependent.firstName || ''} ${
      dependent.lastName || ''
    }`.trim();
    const isDependentOpen = openedTabs.includes(`DEPENDENT-${index}`);

    let subtypeLabel = 'Child / dependent';

    if (dependent.subtype === InsuredSubtype.OTHER_DEPENDENT) {
      subtypeLabel = 'Dependent';
    } else if (dependent.subtype === InsuredSubtype.CHILD) {
      subtypeLabel = 'Child';
    }

    if (subtypeLabel === 'Child / dependent' && isMobile) {
      subtypeLabel = 'Dependent';
    }

    if (fullName && !isDependentOpen) {
      return (
        <>
          <Text mr="xs" fw="600">
            {subtypeLabel}
          </Text>
          <Text fw={400} c="zorroGray.4">
            {fullName}
          </Text>
        </>
      );
    }

    return `${subtypeLabel} (Under 26, or an adult child with a disability)`;
  };

  const renderSectionState = (isSectionValid: boolean) =>
    hasAttemptedSubmit && !isSectionValid ? (
      <Badge color="red">Missing details</Badge>
    ) : null;

  const renderSectionIcon = (icon: ReactNode) => (
    <ThemeIcon size="32" color="indigo">
      {icon}
    </ThemeIcon>
  );

  return (
    <Accordion
      multiple
      value={openedTabs}
      onChange={handleChange}
      styleVariant={accordionStyleVariant}
      withDividerOnOpen={isUnderlined}
    >
      <Accordion.Item value="EMPLOYEE">
        <Accordion.Control
          px={controlPx}
          icon={renderSectionIcon(<IconUser size={16} stroke={1.5} />)}
        >
          <Group gap="xs">
            <Text size="sm" fw={600}>
              <Text mr="xs" fw="600">
                {employeeLabel}
              </Text>
              {!openedTabs.includes('EMPLOYEE') ? (
                <Text fw={400} c="zorroGray.4">
                  {fullNameEmployee}
                </Text>
              ) : undefined}
            </Text>
            {renderSectionState(formValidity.isEmployeeValid)}
          </Group>
        </Accordion.Control>
        <Accordion.Panel px={panelPx} styles={panelStyles}>
          <EmployeeFormInputs
            employeeForm={employeeForm}
            targetEnrollmentDate={effectiveFrom}
            isFinalizationMode={isFinalizationMode}
            isEmployeeMode={isEmployeeMode}
            employerId={employerId}
            setPossibleFipsCodes={insuredForm.setPossibleFipsCodes}
            possibleFipsCodes={insuredForm.possibleFipsCodes}
            isGlobalMode={isGlobalMode}
            withAnticipatedCareLevel={withAnticipatedCareLevel}
            withResidentialAddress={withResidentialAddress}
            onAddressChange={() => handleSendEvent('change_address')}
          />
        </Accordion.Panel>
      </Accordion.Item>
      {spouseStatus.isSpouseActive && (
        <Accordion.Item
          value="SPOUSE"
          mt={sectionMt}
          style={ADDED_SECTION_STYLE}
          ref={spouseSectionRef}
        >
          <Accordion.Control
            px={controlPx}
            icon={renderSectionIcon(<IconUsers size={16} stroke={1.5} />)}
          >
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm" fw="600" className="fs-mask">
                  <Text mr="xs" fw="600">
                    {spouseSubtypeLabel}
                  </Text>
                  {!openedTabs.includes('SPOUSE') ? (
                    <Text fw={400} c="zorroGray.4">
                      {fullNameSpouse}
                    </Text>
                  ) : undefined}
                </Text>
                {renderSectionState(formValidity.isSpouseValid)}
              </Group>
              <IconTrash
                size={20}
                color={brand.zorroGray800}
                onClick={async (event) => {
                  event.preventDefault();
                  await handleDeleteSpouse();
                }}
              />
            </Group>
          </Accordion.Control>
          <Accordion.Panel px={panelPx} styles={panelStyles}>
            <SpouseFormInputs
              spouseForm={spouseForm}
              isSpouseActive={spouseStatus.isSpouseActive}
              isEmployeeMode={isEmployeeMode}
              isFinalizationMode={isFinalizationMode}
              targetEnrollmentDate={effectiveFrom}
              isDomesticPartnerAllowed={isDomesticPartnerAllowed}
              possibleFipsCodes={insuredForm.spousePossibleFipsCodes}
              setPossibleFipsCodes={insuredForm.setSpousePossibleFipsCodes}
              withAnticipatedCareLevel={withAnticipatedCareLevel}
              withResidentialAddress={withResidentialAddress}
            />
          </Accordion.Panel>
        </Accordion.Item>
      )}
      {dependents.map((dependent, index) => (
        <Accordion.Item
          value={`DEPENDENT-${index}`}
          key={`DEPENDENT-${dependent.id}`}
          mt={sectionMt}
          style={ADDED_SECTION_STYLE}
          ref={
            index === dependents.length - 1
              ? lastDependentSectionRef
              : undefined
          }
        >
          {/* Top-aligned so the long open-state title wraps in place; the
              offsets center the first line on the 32px icon. */}
          <Accordion.Control
            px={controlPx}
            icon={renderSectionIcon(<IconMoodBoy size={16} stroke={1.5} />)}
            styles={{
              control: { alignItems: 'flex-start' },
              chevron: { marginTop: 4 },
            }}
          >
            <Group
              justify="space-between"
              align="flex-start"
              wrap="nowrap"
              pt={6}
            >
              <Group gap="xs">
                {/* The accordion label is nowrap; the long open-state title would widen the page on mobile. */}
                <Text
                  size="sm"
                  fw="600"
                  className="fs-mask"
                  style={{ whiteSpace: 'normal' }}
                >
                  {getDependentTitle(index, isMobile)}
                </Text>
                {renderSectionState(
                  getIsDependentValid(dependentSchema, dependentValues[index])
                )}
              </Group>
              <IconTrash
                size={20}
                color={brand.zorroGray400}
                style={{ flexShrink: 0 }}
                onClick={async (event) => {
                  event.preventDefault();
                  remove(index);
                  await handleSendEvent('remove_dependent');
                }}
              />
            </Group>
          </Accordion.Control>
          <Accordion.Panel px={panelPx} styles={panelStyles}>
            <DependentForm
              targetEnrollmentDate={effectiveFrom}
              isFinalizationMode={isFinalizationMode}
              isEmployeeMode={isEmployeeMode}
              dependentsForm={dependentsForm}
              index={index}
              possibleFipsCodes={
                insuredForm.dependentPossibleFipsCodes[index] ?? []
              }
              setPossibleFipsCodes={(codes) =>
                insuredForm.setDependentPossibleFipsCodes(index, codes)
              }
              withAnticipatedCareLevel={withAnticipatedCareLevel}
              withResidentialAddress={withResidentialAddress}
            />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
      <Grid mt={isUnderlined ? 'var(--space-20)' : 'var(--space-12)'}>
        {!spouseStatus.isSpouseActive && (
          <Grid.Col span={{ base: 12, xs: 6 }}>
            <Button
              leftSection={<Icon icon={IconUserHeart} />}
              onClick={() => handleAddSpouse()}
              variant="secondary"
              size="lg"
              w="100%"
            >
              {isDomesticPartnerAllowed
                ? 'Add spouse / domestic partner'
                : 'Add legal spouse'}
            </Button>
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, xs: 6 }}>
          <Button
            leftSection={<Icon icon={IconMoodBoy} />}
            onClick={handleAppendDependent}
            variant="secondary"
            size="lg"
            w="100%"
          >
            Add dependent
          </Button>
        </Grid.Col>
      </Grid>
    </Accordion>
  );
};
